import type { ReactNode } from 'react';
import { useState } from 'react';
import { t } from '@/messages/zh-TW';

/**
 * 第二層版型：側邊功能選單（第一層模組 → 第二層功能項）。
 *
 * 原系統對應：`layout/menu_general.jsp`
 *   `:8-10`   `<nav class="navbar-default navbar-static-side"><div class="sidebar-collapse"><ul class="nav metismenu" id="side-menu">`
 *   `:11-15`  `<li class="nav-header">` 內是 logo（`loadImage/main`）
 *   `:16-18`  第一層 `<li class="root-menu"><a href="#"><i class="fa ..."></i><span class="nav-label">…</span><span class="fa arrow"></span></a>`
 *   `:19`     第二層 `<ul class="nav nav-second-level collapse">`
 *   `:23-28`  功能項 `<li class="sub-menu"><a href="…"><span class="sub-menu-spacer"></span>…</a></li>`
 *   權限包在 `<shiro:hasPermission>` 內 —— 雛形不做權限判斷，由頁面層先過濾好再傳進來。
 *
 * 選單資料來源是 `analysis/menu-tree.json`（20 個第一層模組、115 個功能項），
 * 但**本元件不讀檔**：只吃 props，資料由頁面層注入。
 *
 * ponytail: 展開行為照原系統 metisMenu 預設（accordion，一次只開一組），用單一
 * 「目前展開的 group id」state 即可，不做多組同時展開的受控／非受控雙模式。
 * 升級路徑：真的需要多組同時展開時，把 state 換成 Set 並加 `expandedIds` 受控 prop。
 *
 * 【證據等級】§7 第 3 級（原始碼）。【雛形值待實機校準】
 */
export interface SidebarMenuItem {
  id: string;
  label: string;
  /** 原系統的相對路徑，例如 `config/role`。本元件不解析也不判斷，原樣放進 href。 */
  href: string;
}

export interface SidebarMenuGroup {
  id: string;
  label: string;
  /** Font Awesome 圖示名稱，例如 `home`、`file-o`（menu_general.jsp:17 首頁 fa-home、:96 表單精靈 fa-file-o）。 */
  icon?: string;
  items: readonly SidebarMenuItem[];
}

export interface SidebarMenuProps {
  uiId: string;
  groups: readonly SidebarMenuGroup[];
  /** 目前所在功能項；其所屬群組預設展開。 */
  activeItemId?: string;
  /** 收合狀態（`body.mini-navbar`，css/style.css:349-352 會隱藏 `.nav-label`）。 */
  collapsed?: boolean;
  /** logo 區（menu_general.jsp:11-15）。 */
  brand?: ReactNode;
  onNavigate?(item: SidebarMenuItem): void;
}

function initialExpandedId(
  groups: readonly SidebarMenuGroup[],
  activeItemId: string | undefined,
): string | null {
  if (activeItemId === undefined) return null;
  const found = groups.find((group) => group.items.some((item) => item.id === activeItemId));
  return found?.id ?? null;
}

export function SidebarMenu({
  uiId,
  groups,
  activeItemId,
  collapsed = false,
  brand,
  onNavigate,
}: SidebarMenuProps) {
  const [expandedId, setExpandedId] = useState<string | null>(() =>
    initialExpandedId(groups, activeItemId),
  );

  return (
    <nav data-ui-id={uiId} aria-label={t('a11y.sidebarMenu')} className="text-surface">
      {brand === undefined ? null : (
        <div data-ui-id={`${uiId}-brand`} className="px-6 py-8">
          {brand}
        </div>
      )}
      <ul data-ui-id={`${uiId}-groups`}>
        {groups.map((group) => {
          const expanded = expandedId === group.id;
          return (
            <li key={group.id} data-ui-id={`${uiId}-group-${group.id}`}>
              <button
                type="button"
                data-ui-id={`${uiId}-group-${group.id}-toggle`}
                aria-expanded={expanded}
                aria-controls={`${uiId}-group-${group.id}-items`}
                onClick={() => setExpandedId(expanded ? null : group.id)}
                className="flex w-full items-center gap-3 px-6 py-3 text-left hover:bg-shell-deep"
              >
                {group.icon === undefined ? null : (
                  <span
                    data-ui-id={`${uiId}-group-${group.id}-icon`}
                    aria-hidden="true"
                    className="w-4 shrink-0 text-center"
                  >
                    {/* ponytail: 雛形不引入 Font Awesome（§3.2 禁外部資產），先用首字母佔位，
                        圖示名稱原樣保留在 props 與 data 屬性，日後換成 <Icon> 只改這一行。 */}
                    {group.icon.slice(0, 1).toUpperCase()}
                  </span>
                )}
                {collapsed ? null : (
                  <span data-ui-id={`${uiId}-group-${group.id}-label`} className="flex-1 truncate">
                    {group.label}
                  </span>
                )}
                <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>
              </button>
              <ul
                id={`${uiId}-group-${group.id}-items`}
                data-ui-id={`${uiId}-group-${group.id}-items`}
                hidden={!expanded}
                className="bg-shell-deep"
              >
                {group.items.map((item) => (
                  <li key={item.id} data-ui-id={`${uiId}-item-${item.id}`}>
                    <a
                      href={item.href}
                      data-ui-id={`${uiId}-item-${item.id}-link`}
                      aria-current={item.id === activeItemId ? 'page' : undefined}
                      onClick={() => onNavigate?.(item)}
                      className={
                        item.id === activeItemId
                          ? 'block bg-black/40 py-2 pr-4 pl-14 text-sm'
                          : 'block py-2 pr-4 pl-14 text-sm hover:bg-black/20'
                      }
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
